// Reading the MP3 files out of a ZIP in the browser (user request 2026-10-03): the admin gives a ZIP of
// songs, the browser unpacks it one file at a time (File.slice – the whole ZIP is never in memory) and
// uploads the songs; the ZIP itself never reaches the server. Stored and deflated entries (ZIP64 not).

const u16 = (v: DataView, at: number) => v.getUint16(at, true);
const u32 = (v: DataView, at: number) => v.getUint32(at, true);

async function bytes(file: Blob, from: number, to: number): Promise<DataView> {
  return new DataView(await file.slice(from, to).arrayBuffer());
}

/** The MP3 files of the ZIP, as File objects named by their base name. */
export async function mp3sFromZip(zip: File): Promise<File[]> {
  // The end of central directory record is in the last 64 kB (+ 22 bytes).
  const tailFrom = Math.max(0, zip.size - 65_557);
  const tail = await bytes(zip, tailFrom, zip.size);
  let end = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (u32(tail, i) === 0x06054b50) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new Error('zip');
  const count = u16(tail, end + 10);
  const dirSize = u32(tail, end + 12);
  const dirAt = u32(tail, end + 16);
  const dir = await bytes(zip, dirAt, dirAt + dirSize);
  const decoder = new TextDecoder();
  const files: File[] = [];
  let p = 0;
  for (let n = 0; n < count && p + 46 <= dir.byteLength; n++) {
    if (u32(dir, p) !== 0x02014b50) throw new Error('zip');
    const method = u16(dir, p + 10);
    const size = u32(dir, p + 20);
    const nameLen = u16(dir, p + 28);
    const extraLen = u16(dir, p + 30);
    const commentLen = u16(dir, p + 32);
    const localAt = u32(dir, p + 42);
    const name = decoder.decode(new Uint8Array(dir.buffer, dir.byteOffset + p + 46, nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    // Windows' own ZIP tool writes backslashes between the folders.
    const base = name.split(/[\\/]/).pop() ?? '';
    if (!/\.mp3$/i.test(base) || base.startsWith('._') || (method !== 0 && method !== 8)) continue;
    const local = await bytes(zip, localAt, localAt + 30);
    const dataAt = localAt + 30 + u16(local, 26) + u16(local, 28);
    const raw = zip.slice(dataAt, dataAt + size);
    const data = method === 0 ? raw : await new Response(raw.stream().pipeThrough(new DecompressionStream('deflate-raw'))).blob();
    files.push(new File([data], base, { type: 'audio/mpeg' }));
  }
  return files;
}
